import {
    BUSINESS_ERROR_RULES,
    BusinessAction,
    BusinessCompanyStatus,
    BusinessErrorCode,
    BusinessFailure,
    BusinessLookupEligible,
    BusinessRejectionReason,
    BusinessSubmitSuccess,
    BusinessTransportResult,
} from './contract';

export type UnknownReason =
    | 'network'
    | 'non_json'
    | 'malformed'
    | 'unknown_code'
    | 'contradiction';

export type ParsedBusinessResult =
    | { kind: 'eligible'; response: BusinessLookupEligible }
    | { kind: 'submitted'; response: BusinessSubmitSuccess }
    | { kind: 'failure'; response: BusinessFailure; retryAfterSeconds?: number }
    | { kind: 'unknown'; reason: UnknownReason };

const unknown = (reason: UnknownReason): ParsedBusinessResult => ({
    kind: 'unknown',
    reason,
});

/** What callers use when the request itself rejected (network failure, timeout). */
export const UNKNOWN_NETWORK_RESULT: ParsedBusinessResult = unknown('network');

const ACTIONS: readonly BusinessAction[] = [
    'EDIT_CUI',
    'RETRY_LOOKUP',
    'SWITCH_TO_INDIVIDUAL',
    'CONTINUE_WITHOUT_BILLING',
    'RESTART_FROM_TERMINAL',
];
const REASONS: readonly BusinessRejectionReason[] = [
    'FISCALLY_INACTIVE',
    'REGISTRATION_SUSPENDED',
    'REGISTRATION_DISSOLVED',
    'REGISTRATION_STRUCK_OFF',
];
const REGISTRATION_STATUSES = ['registered', 'suspended', 'dissolved', 'struck_off', 'unknown'];
const FISCAL_STATUSES = ['active', 'inactive', 'unknown'];

type Obj = Record<string, unknown>;

const isObject = (value: unknown): value is Obj =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown): value is string =>
    typeof value === 'string' && value.trim() !== '';
const isOptionalText = (value: unknown): boolean =>
    value === undefined || typeof value === 'string';
const isTimestamp = (value: unknown): value is string =>
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(value) &&
    !Number.isNaN(Date.parse(value));
const isDateOnly = (value: unknown): value is string =>
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(Date.parse(value));
const isErrorCode = (value: unknown): value is BusinessErrorCode =>
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(BUSINESS_ERROR_RULES, value);

const validCompany = (company: unknown): boolean => {
    if (!isObject(company) || company.entityType !== 'business' || !isText(company.name)) {
        return false;
    }
    const { tax, address } = company;
    if (
        !isObject(tax) ||
        tax.countryCode !== 'RO' ||
        typeof tax.cui !== 'string' ||
        !/^[0-9]{2,10}$/.test(tax.cui) ||
        typeof tax.vatRegistered !== 'boolean' ||
        !isText(tax.invoiceIdentifier)
    ) {
        return false;
    }
    return (
        isObject(address) &&
        address.countryCode === 'RO' &&
        isText(address.region) &&
        isText(address.city) &&
        isText(address.line1) &&
        isText(address.rawAddress) &&
        address.source === 'anaf' &&
        (address.addressKind === 'fiscal' || address.addressKind === 'registered_office') &&
        isOptionalText(address.line2) &&
        isOptionalText(address.postalCode)
    );
};

const validVerification = (verification: unknown): boolean =>
    isObject(verification) &&
    isText(verification.reference) &&
    verification.provider === 'anaf' &&
    isTimestamp(verification.checkedAt) &&
    isTimestamp(verification.expiresAt) &&
    isDateOnly(verification.effectiveDate);

const readCompanyStatus = (value: unknown): BusinessCompanyStatus | undefined => {
    if (
        isObject(value) &&
        REGISTRATION_STATUSES.includes(value.registrationStatus as string) &&
        FISCAL_STATUSES.includes(value.fiscalStatus as string) &&
        (value.registrationStatusRaw === null || typeof value.registrationStatusRaw === 'string')
    ) {
        return value as unknown as BusinessCompanyStatus;
    }
    return undefined;
};

const parseEligible = (body: Obj, status: number | undefined): ParsedBusinessResult => {
    const companyStatus = body.companyStatus;
    if (
        body.operation !== 'lookup' ||
        body.canSubmitBusiness !== true ||
        (status !== undefined && status !== 200) ||
        (body.error !== undefined && body.error !== null) ||
        !validCompany(body.company) ||
        !validVerification(body.verification) ||
        !isObject(companyStatus) ||
        companyStatus.registrationStatus !== 'registered' ||
        companyStatus.fiscalStatus !== 'active'
    ) {
        return unknown(body.canSubmitBusiness === false ? 'contradiction' : 'malformed');
    }
    return { kind: 'eligible', response: body as unknown as BusinessLookupEligible };
};

const parseSubmitted = (
    body: Obj,
    actions: BusinessAction[],
    status: number | undefined,
): ParsedBusinessResult => {
    const submission = body.submission;
    if (
        body.operation !== 'submit' ||
        body.canSubmitBusiness !== false ||
        actions.length !== 0 ||
        (status !== undefined && status !== 200) ||
        (body.error !== undefined && body.error !== null) ||
        !isObject(submission) ||
        !isText(submission.reference) ||
        !isTimestamp(submission.submittedAt)
    ) {
        return unknown('malformed');
    }
    return { kind: 'submitted', response: body as unknown as BusinessSubmitSuccess };
};

const parseFailure = (
    body: Obj,
    actions: BusinessAction[],
    status: number | undefined,
): ParsedBusinessResult => {
    const error = body.error;
    if (!isObject(error)) {
        return unknown('malformed');
    }
    if (!isErrorCode(error.code)) {
        return unknown('unknown_code');
    }
    const code = error.code;
    const rule = BUSINESS_ERROR_RULES[code];
    if (body.outcome !== rule.outcome || body.canSubmitBusiness !== false) {
        return unknown('contradiction');
    }
    if (
        typeof error.retryable !== 'boolean' ||
        (error.field !== 'cui' && error.field !== null) ||
        !Array.isArray(error.reasons)
    ) {
        return unknown('malformed');
    }

    let retryAfterSeconds: number | undefined;
    if (code === 'LOOKUP_RATE_LIMITED') {
        const seconds = error.retryAfterSeconds;
        if (typeof seconds !== 'number' || !Number.isInteger(seconds) || seconds <= 0) {
            return unknown('malformed');
        }
        retryAfterSeconds = seconds;
    }
    void status; // status is transport metadata; the error code decides.

    const reasons = (error.reasons as unknown[]).filter((reason): reason is BusinessRejectionReason =>
        REASONS.includes(reason as BusinessRejectionReason),
    );
    const companyStatus = readCompanyStatus(body.companyStatus);
    // Rebuilt from validated parts: company / verification are never carried over.
    const response = {
        requestId: body.requestId,
        operation: body.operation,
        outcome: rule.outcome,
        canSubmitBusiness: false,
        allowedActions: actions,
        error: {
            code,
            field: error.field,
            retryable: error.retryable,
            reasons,
            ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
        },
        ...(companyStatus ? { companyStatus } : {}),
    } as unknown as BusinessFailure;
    return {
        kind: 'failure',
        response,
        ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
    };
};

const parse = (body: unknown, status: number | undefined): ParsedBusinessResult => {
    if (body === undefined) {
        return unknown('non_json');
    }
    if (
        !isObject(body) ||
        !isText(body.requestId) ||
        (body.operation !== 'lookup' && body.operation !== 'submit') ||
        typeof body.canSubmitBusiness !== 'boolean' ||
        !Array.isArray(body.allowedActions)
    ) {
        return unknown('malformed');
    }
    const actions = (body.allowedActions as unknown[]).filter((action): action is BusinessAction =>
        ACTIONS.includes(action as BusinessAction),
    );
    if (body.outcome === 'eligible') {
        return parseEligible({ ...body, allowedActions: actions }, status);
    }
    if (body.outcome === 'submitted') {
        return parseSubmitted(body, actions, status);
    }
    return parseFailure(body, actions, status);
};

/** For `businessState` restored on page reload: any operation, no HTTP status. */
export const parseBusinessEnvelope = (body: unknown): ParsedBusinessResult =>
    parse(body, undefined);

/**
 * Parses the same envelope whether the status is 200 or not. The error code and
 * outcome decide, never the HTTP status or any backend prose.
 */
export const parseBusinessTransportResult = (
    result: BusinessTransportResult,
    expectedOperation: 'lookup' | 'submit',
): ParsedBusinessResult => {
    const parsed = parse(result.body, result.status);
    if (parsed.kind !== 'unknown' && parsed.response.operation !== expectedOperation) {
        return unknown('contradiction');
    }
    return parsed;
};
